import type { RegulationDocumentStatus, SuccessRegulationStatusWithLinks } from 'state/quotes/dashboard/model';
import * as fromRegulation from 'state/quotes/regulations/regulation.selectors';
import { getFormQuoteId } from 'state/selectors';
import type { AppState } from 'state/state.model';
import type { QuoteWorkspaceType } from 'state/workspaces/model';
export function getQuoteRegulationInfos(state: AppState, workspace: QuoteWorkspaceType, tabId: string) {
  const quoteId = getFormQuoteId(state, workspace, tabId);
  return fromRegulation.getRegulationInfosOrDefault(state, tabId, quoteId);
}
export function getQuoteIsKidExpected(state: AppState, tabId: string, quoteId: string | undefined): boolean {
  if (quoteId === undefined) {
    return false;
  }
  return fromRegulation.getRegulationInfos(state, tabId, quoteId)?.kidExpected ?? false;
}

export function getQuoteRegulationKidDocument(state: AppState, tabId: string, quoteId: string) {
  return fromRegulation.getRegulationInfos(state, tabId, quoteId)?.kidDocument;
}

export function getQuoteRegulationKidDocumentError(state: AppState, tabId: string, quoteId: string) {
  return fromRegulation.getRegulationInfos(state, tabId, quoteId)?.kidDocumentError;
}

export function getQuoteRegulationProductGovernanceDocumentation(state: AppState, tabId: string, quoteId: string) {
  return fromRegulation.getRegulationInfos(state, tabId, quoteId)?.productGovernanceDocumentation;
}

export function getQuoteRegulationProductGovernanceDocumentationError(state: AppState, tabId: string, quoteId: string) {
  return fromRegulation.getRegulationInfos(state, tabId, quoteId)?.productGovernanceDocumentationError;
}

export function getQuoteRegulationProductGovernanceDocumentationTimeout(
  state: AppState,
  tabId: string,
  quoteId: string,
) {
  return fromRegulation.getRegulationInfos(state, tabId, quoteId)?.productGovernanceDocumentationTimeout;
}

export function getQuoteRegulationProductGovernanceAssessment(state: AppState, tabId: string, quoteId: string) {
  return fromRegulation.getRegulationInfos(state, tabId, quoteId)?.productGovernanceAssessment;
}

export function getQuoteRegulationProductGovernanceAssessmentTimeout(state: AppState, tabId: string, quoteId: string) {
  return fromRegulation.getRegulationInfos(state, tabId, quoteId)?.productGovernanceAssessmentTimeout;
}

export function hasProductGovernanceErrors(state: AppState, tabId: string, quoteId: string) {
  const productGovernanceDocumentationError = getQuoteRegulationProductGovernanceDocumentationError(
    state,
    tabId,
    quoteId,
  );
  return productGovernanceDocumentationError?.some(x => !!x) ?? false;
}

export function hasProductGovernanceAssessmentErrors(state: AppState, tabId: string, quoteId: string) {
  const productGovernanceDocumentationError = getQuoteRegulationProductGovernanceAssessment(state, tabId, quoteId);
  return productGovernanceDocumentationError?.rejections?.some(x => !!x) ?? false;
}

export function hasAnyProductGovernance(state: AppState, tabId: string, quoteId: string) {
  const productGovernanceAssessment = getQuoteRegulationProductGovernanceAssessment(state, tabId, quoteId);
  const productGovernanceDocumentation = getQuoteRegulationProductGovernanceDocumentation(state, tabId, quoteId);
  return productGovernanceAssessment !== undefined || productGovernanceDocumentation !== undefined;
}

export function getTargetMarketStatus(state: AppState, tabId: string, quoteId: string): RegulationDocumentStatus {
  const productGovernanceAssessment = getQuoteRegulationProductGovernanceAssessment(state, tabId, quoteId);
  const productGovernanceDocumentation = getQuoteRegulationProductGovernanceDocumentation(state, tabId, quoteId);

  if (productGovernanceAssessment?.client.product.vfm === false) {
    return 'Failure';
  }
  if (productGovernanceAssessment === undefined || productGovernanceDocumentation === undefined) {
    return 'Pending';
  }

  const status: SuccessRegulationStatusWithLinks = {
    status: 'Success',
    links: productGovernanceDocumentation!.map(doc => [doc.language, doc.uri]),
  };

  return status;
}

export function isProductGovernanceDocumentationInError(state: AppState, tabId: string, quoteId: string) {
  const hasPGErrors = hasProductGovernanceErrors(state, tabId, quoteId);
  const isPGTimeout = getQuoteRegulationProductGovernanceDocumentationTimeout(state, tabId, quoteId) ?? false;
  return hasPGErrors || isPGTimeout;
}

export function isProductGovernanceAssessmentInError(state: AppState, tabId: string, quoteId: string) {
  const hasPGAssErrors = hasProductGovernanceAssessmentErrors(state, tabId, quoteId);
  const productGovernanceAssessmentTimeout =
    getQuoteRegulationProductGovernanceAssessmentTimeout(state, tabId, quoteId) ?? false;
  return hasPGAssErrors || productGovernanceAssessmentTimeout;
}

export function hasAnyErrorInProductGovernance(state: AppState, tabId: string, quoteId: string) {
  const isDocumentationInError = isProductGovernanceDocumentationInError(state, tabId, quoteId);
  const isAssessmentInError = isProductGovernanceAssessmentInError(state, tabId, quoteId);

  return isDocumentationInError || isAssessmentInError;
}

export function hasQuoteRegulationKidErrors(state: AppState, tabId: string, quoteId: string) {
  const kidDocumentError = getQuoteRegulationKidDocumentError(state, tabId, quoteId);
  const kidDocumentTimeout = getQuoteRegulationKidDocumentTimeout(state, tabId, quoteId);
  return (kidDocumentError?.some(x => !!x) ?? false) || kidDocumentTimeout;
}

export function hasQuoteRegulationKidDocument(state: AppState, tabId: string, quoteId: string) {
  const kidDocument = getQuoteRegulationKidDocument(state, tabId, quoteId);
  return kidDocument !== undefined;
}

export function getQuoteRegulationKidDocumentTimeout(state: AppState, tabId: string, quoteId: string) {
  return fromRegulation.getRegulationInfos(state, tabId, quoteId)?.kidDocumentTimeout ?? false;
}

export function getKidTimeoutMs() {
  return window.sgmeConfiguration.kidTimeoutMs;
}

export function getProductGovernanceTimeoutMs() {
  return window.sgmeConfiguration.pgTimeoutMs;
}

export function getQuoteRegulationThoreticalValue(state: AppState, tabId: string, quoteId: string): string | undefined {
  const rawValue = fromRegulation
    .getRegulationInfos(state, tabId, quoteId)
    ?.theoreticalValue?.find(i => i.name === 'JapaneseFairValue')?.value;
  return rawValue ? rawValue + '%' : undefined;
}
